import {
  AlertOctagon,
  AlertTriangle,
  Loader2,
  TrendingUp,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type { SystemState, SystemStateResult } from "../../hooks/useSystemState";
import { cn } from "@/lib/utils";
import ScalingConfirm from "./ScalingConfirm";

/**
 * Banner shown above the dashboard grid for any non-HEALTHY state.
 *
 * <ul>
 *   <li>SPEC §19.18 — {@code SAFE_SUSPENDED} after H2 loss; sticky until
 *       manual acknowledgement.</li>
 *   <li>SPEC §19.19 — {@code DEGRADED} when IG reconciliation fails;
 *       no new entries.</li>
 *   <li>SPEC §19.22 — {@code SCALING_REVIEW} after a &gt;50% balance jump
 *       in 24h; position size frozen until OTP confirmation (F4 action).</li>
 *   <li>Partial-un-stubbing rule (B5b): when {@code /api/system/state}
 *       errors out, render an explicit ERROR banner — never silently
 *       fall back to "HEALTHY".</li>
 *   <li>B7.0: a {@code connecting} poll (ingress 502/503/504 — pod
 *       starting or rolling) shows a neutral "connecting" notice, not
 *       the red ERROR banner.</li>
 * </ul>
 */

const SHELL =
  "state-banner flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-4 py-2.5 text-sm";

function tone(variant: "info" | "amber" | "red"): string {
  switch (variant) {
    case "info":
      return "border-info/40 bg-info-soft text-foreground";
    case "amber":
      return "border-warning/40 bg-warning-soft text-foreground";
    case "red":
      return "border-sell/50 bg-sell-soft text-foreground";
  }
}

export default function StateBanner({ state, error, connecting }: SystemStateResult) {
  if (connecting && !error) {
    return (
      <div
        className={cn(SHELL, "state-banner--connecting", tone("info"))}
        role="status"
      >
        <Loader2 className="text-info size-4 shrink-0 animate-spin" aria-hidden />
        <strong>CONNECTING…</strong>
        <span className="text-muted-foreground">
          Le backend démarre ou est brièvement injoignable. Reconnexion — l'état
          système s'affichera dès qu'il répond.
        </span>
      </div>
    );
  }
  if (error) {
    return (
      <div
        className={cn(SHELL, "state-banner--error", tone("red"))}
        role="alert"
      >
        <AlertOctagon className="text-sell size-4 shrink-0" aria-hidden />
        <strong>SYSTEM_STATE_UNAVAILABLE.</strong>
        <span className="text-muted-foreground">
          /api/system/state injoignable. L'état réel est inconnu — considère les
          bannières comme périmées tant que la connexion n'est pas rétablie.
        </span>
      </div>
    );
  }
  if (state == null) return null; // first load in flight, banner suppressed
  if (state === "HEALTHY") return null;
  return <NonHealthyBanner state={state} />;
}

function BannerIcon({ Icon, className }: { Icon: LucideIcon; className: string }) {
  return <Icon className={cn("size-4 shrink-0", className)} aria-hidden />;
}

function NonHealthyBanner({ state }: { state: Exclude<SystemState, "HEALTHY"> }) {
  switch (state) {
    case "SAFE_SUSPENDED":
      return (
        <div
          className={cn(SHELL, "state-banner--suspended", tone("amber"))}
          role="alert"
        >
          <BannerIcon Icon={AlertTriangle} className="text-warning" />
          <strong>SAFE_SUSPENDED.</strong>
          <span className="text-muted-foreground">
            Perte du store opérationnel détectée. Trading stoppé. Acquittement
            manuel requis avant reprise (SPEC §19.18).
          </span>
        </div>
      );
    case "DEGRADED":
      return (
        <div
          className={cn(SHELL, "state-banner--degraded", tone("amber"))}
          role="alert"
        >
          <BannerIcon Icon={AlertTriangle} className="text-warning" />
          <strong>DEGRADED.</strong>
          <span className="text-muted-foreground">
            Réconciliation IG en échec. Aucune nouvelle entrée ne sera ouverte.
            Les stops côté IG restent en vigueur (SPEC §19.19).
          </span>
        </div>
      );
    case "SCALING_REVIEW":
      return (
        <div
          className={cn(SHELL, "state-banner--scaling", tone("amber"))}
          role="alert"
        >
          <BannerIcon Icon={TrendingUp} className="text-warning" />
          <strong>SCALING_REVIEW.</strong>
          <span className="text-muted-foreground flex-1">
            Solde en hausse de &gt;50 % en 24 h. Taille de position gelée jusqu'à
            confirmation du scaling (SPEC §19.22).
          </span>
          <ScalingConfirm />
        </div>
      );
  }
}
